import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { type Duenio, type DuenioRequest } from '../types/duenio';
import { DuenioService } from '../services/duenioService';

interface DuenioState {
  duenios: Duenio[];
  loading: boolean;
  error: string | null;
  selectedDuenio: Duenio | null;
  dialogOpen: boolean;
  fetchDuenios: () => Promise<void>;
  saveDuenio: (duenio: DuenioRequest) => Promise<void>;
  deleteDuenio: (id: number) => Promise<void>;
  setSelectedDuenio: (duenio: Duenio | null) => void;
  openDialog: () => void;
  closeDialog: () => void;
  clearError: () => void;
}

export const useDuenioStore = create<DuenioState>()(
  devtools(
    (set, get) => ({
      duenios: [],
      loading: false,
      error: null,
      selectedDuenio: null,
      dialogOpen: false,

      fetchDuenios: async () => {
        set({ loading: true, error: null });
        try {
          const duenios = await DuenioService.getDuenios();
          set({ duenios, loading: false });
        } catch (error) {
          set({ error: 'Error al cargar dueños', loading: false });
          console.error('Error fetching duenios:', error);
        }
      },

      saveDuenio: async (duenio: DuenioRequest) => {
        set({ loading: true, error: null });
        try {
          await DuenioService.saveDuenio(duenio);
          await get().fetchDuenios();
          set({ loading: false, dialogOpen: false, selectedDuenio: null });
        } catch (error) {
          set({ error: 'Error al guardar dueño', loading: false });
          console.error('Error saving duenio:', error);
          throw error;
        }
      },

      deleteDuenio: async (id: number) => {
        set({ loading: true, error: null });
        try {
          await DuenioService.deleteDuenio(id);
          set((state) => ({
            duenios: state.duenios.filter((d) => d.id !== id),
            loading: false,
          }));
        } catch (error) {
          set({ error: 'Error al eliminar dueño', loading: false });
          console.error('Error deleting duenio:', error);
          throw error;
        }
      },

      setSelectedDuenio: (duenio: Duenio | null) => set({ selectedDuenio: duenio }),
      openDialog: () => set({ dialogOpen: true }),
      closeDialog: () => set({ dialogOpen: false, selectedDuenio: null }),
      clearError: () => set({ error: null }),
    }),
    { name: 'duenio-store' }
  )
);
